/**
 * MessagesPage Component
 *
 * Slack-style unified messaging UI with sidebar (conversation list) and
 * an active chat panel. Supports project chats, direct messages, and
 * group conversations on the same surface.
 *
 * Routes:
 *   /mensajes                 - sidebar only (empty chat panel)
 *   /mensajes/:conversationId - sidebar + active chat
 *
 * @module pages/messages/MessagesPage
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Spinner,
  Dropdown,
  Modal,
  Button,
  Form,
  Alert,
} from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import {
  CONVERSATION_TYPE,
  listConversations,
  getConversation,
  getMessages,
  sendMessage,
  uploadChatAttachment,
  markConversationRead,
  subscribeToConversation,
  subscribeToConversations,
  getUnreadCounts,
  leaveConversation,
  deleteConversation,
  muteConversation,
  addParticipant,
  getParticipants,
  shareMessageToBitacora,
  deleteMessage,
  editMessage,
} from '../../services/chatService';
import { supabase } from '../../services/supabase';
import { toast } from '../../utils/toast';
import { formatUserRole } from '../../utils/formatters';
import TaskAssigneeAvatar from '../../components/tasks/shared/TaskAssigneeAvatar';
import KebabMenu from '../../components/common/KebabMenu';
import MessageList from '../technician/messages/MessageList';
import ChatInput from '../technician/messages/ChatInput';
import NewDmModal from '../../components/messages/NewDmModal';
import NewGroupModal from '../../components/messages/NewGroupModal';
import NewChannelModal from '../../components/messages/NewChannelModal';
import MessagesSearchBar from '../../components/messages/MessagesSearchBar';
import BitacoraLinkModal from '../../components/chat/BitacoraLinkModal';
import DashboardLayout from '../../components/layout/DashboardLayout';
import '../technician/messages/ProjectChat.css';
import './Messages.css';

/**
 * Format a timestamp into a compact relative-ish indicator (HH:mm,
 * weekday for last week, otherwise short date).
 */
const formatLastMessageTime = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  if (sameDay) {
    return new Intl.DateTimeFormat('es-CR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);
  }
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  if (diffDays < 7) {
    return new Intl.DateTimeFormat('es-CR', { weekday: 'short' }).format(date);
  }
  return new Intl.DateTimeFormat('es-CR', {
    day: '2-digit',
    month: '2-digit',
  }).format(date);
};

/**
 * Resolve the "other participant" for each DM in a conversations list by
 * batch-querying chat_participants. Returns `{ [conversationId]: profile }`.
 */
const fetchDmCounterparts = async (conversations, currentUserId) => {
  const dmIds = (conversations || [])
    .filter((c) => c.type === CONVERSATION_TYPE.DM)
    .map((c) => c.id);
  if (dmIds.length === 0) return {};

  const { data, error } = await supabase
    .from('chat_participants')
    .select(`
      conversation_id,
      user_id,
      user:profiles!user_id(id, full_name, avatar_url, role)
    `)
    .in('conversation_id', dmIds)
    .neq('user_id', currentUserId);
  if (error) {
    console.error('[MESSAGES] fetchDmCounterparts error:', error);
    return {};
  }
  const map = {};
  for (const row of data || []) {
    if (!map[row.conversation_id]) {
      map[row.conversation_id] = row.user;
    }
  }
  return map;
};

const TAB_FILTERS = [
  { key: 'all', label: 'Todos', type: null },
  { key: 'project', label: 'Proyectos', type: CONVERSATION_TYPE.PROJECT },
  { key: 'dm', label: 'DMs', type: CONVERSATION_TYPE.DM },
  { key: 'group', label: 'Grupos', type: CONVERSATION_TYPE.GROUP },
];

/**
 * Snippet of the conversation's last message for the sidebar row.
 *
 * `listConversations` already returns `lastMessage` (sender + content +
 * message_type) for every conversation, but this page used to throw it away
 * and print the literal string "Ultimo mensaje" — a placeholder where real
 * data was already in hand. Ported from ConversationList (the /admin/mensajes
 * surface removed in #448), which did show it.
 */
const getLastMessagePreview = (conversation) => {
  const msg = conversation.lastMessage;
  if (!msg) return 'Sin mensajes aun';

  const senderName = msg.sender?.full_name?.split(' ')[0] || '';
  const prefix =
    conversation.type !== CONVERSATION_TYPE.DM && senderName
      ? `${senderName}: `
      : '';

  if (msg.message_type === 'image') return `${prefix}Imagen`;
  if (msg.message_type === 'file') return `${prefix}Archivo`;
  if (msg.message_type === 'system') return msg.content || 'Mensaje del sistema';

  const content = msg.content || '';
  return `${prefix}${content.length > 40 ? `${content.substring(0, 40)}...` : content}`;
};

const ConversationRow = ({
  conversation,
  isActive,
  unread,
  otherUser,
  onClick,
  onToggleMute,
  onDelete,
}) => {
  const isDm = conversation.type === CONVERSATION_TYPE.DM;
  const isGroup = conversation.type === CONVERSATION_TYPE.GROUP;
  const isProject = conversation.type === CONVERSATION_TYPE.PROJECT;

  const title = isDm
    ? otherUser?.full_name || 'Mensaje directo'
    : conversation.name ||
      conversation.project?.title ||
      (isGroup ? 'Grupo' : isProject ? 'Proyecto' : 'Conversacion');

  const typePill = isDm ? 'DM' : isGroup ? 'Grupo' : 'Proyecto';
  const time = formatLastMessageTime(conversation.last_message_at);
  const hasUnread = unread > 0;

  const isMuted = !!conversation.isMuted;

  return (
    <div className={`messages-list-item ${isActive ? 'active' : ''}`}>
      <button
        type="button"
        className="messages-list-item-main"
        onClick={onClick}
      >
        <span className="item-avatar">
          {isDm ? (
            <TaskAssigneeAvatar assignee={otherUser} size="lg" />
          ) : (
            <span className="messages-list-item-icon">
              <i
                className={`bi ${isGroup ? 'bi-people' : 'bi-folder-fill'}`}
              ></i>
            </span>
          )}
        </span>
        <div className="messages-list-item-body">
          <div className="messages-list-item-row">
            <span className="messages-list-item-title">{title}</span>
            {isMuted && (
              <i
                className="bi bi-bell-slash messages-list-item-muted"
                role="img"
                title="Silenciada"
                aria-label="Silenciada"
              ></i>
            )}
            <span
              className={`messages-list-item-time ${
                hasUnread ? 'has-unread' : ''
              }`}
            >
              {time}
            </span>
          </div>
          <div className="messages-list-item-meta">
            <span className="messages-list-item-snippet">
              <span className="messages-type-pill">{typePill}</span>
              <span className="messages-list-item-preview">
                {getLastMessagePreview(conversation)}
              </span>
            </span>
            {hasUnread && (
              <span className="messages-unread-badge">
                {unread > 99 ? '99+' : unread}
              </span>
            )}
          </div>
        </div>
      </button>

      {/* Row actions (#448): mute + delete, ported from the retired
          /admin/mensajes sidebar. Unlike that one, this kebab is always
          visible — the original was `opacity: 0` until :hover, which is a
          dead affordance on touch. */}
      <KebabMenu
        label={`Acciones de la conversación ${title}`}
        className="messages-list-item-kebab"
        toggleStyle={{ minWidth: 44, minHeight: 44 }}
      >
        <Dropdown.Item onClick={() => onToggleMute(conversation, !isMuted)}>
          <i className={`bi ${isMuted ? 'bi-bell' : 'bi-bell-slash'} me-2`}></i>
          {isMuted ? 'Activar notificaciones' : 'Silenciar'}
        </Dropdown.Item>
        <Dropdown.Divider />
        <Dropdown.Item
          onClick={() => onDelete(conversation)}
          className="text-danger"
        >
          <i className="bi bi-trash me-2"></i>
          Eliminar
        </Dropdown.Item>
      </KebabMenu>
    </div>
  );
};

const MessagesPage = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState('all');
  const [showNewDm, setShowNewDm] = useState(false);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [showNewChannel, setShowNewChannel] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [addParticipantId, setAddParticipantId] = useState('');
  // #448: conversation-level "vincular a bitacora" (the whole conversation,
  // with a free-text summary) — distinct from the per-message
  // "compartir en bitacora" kebab action already in MessageList.
  const [showBitacoraModal, setShowBitacoraModal] = useState(false);
  // #448: conversation pending deletion from the sidebar kebab.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [sending, setSending] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  // Search bar (#427): while active, the sidebar shows results instead of
  // the tabs/list. `highlightMessageId` drives the "jump to message" scroll
  // + flash once the target conversation's messages are loaded.
  const [searchActive, setSearchActive] = useState(false);
  const [highlightMessageId, setHighlightMessageId] = useState(null);

  const subscriptionRef = useRef(null);

  // -------------------------------------------------------------------
  // Conversations list query
  // -------------------------------------------------------------------
  const {
    data: conversations = [],
    isLoading: convLoading,
    error: convError,
  } = useQuery({
    queryKey: ['conversations'],
    queryFn: async () => {
      const { data, error } = await listConversations();
      if (error) throw error;
      return data || [];
    },
    staleTime: 1000 * 30,
  });

  // -------------------------------------------------------------------
  // DM counterparts (other participant for each DM)
  // -------------------------------------------------------------------
  const { data: dmCounterparts = {} } = useQuery({
    queryKey: ['dm-counterparts', conversations.map((c) => c.id).join(',')],
    queryFn: () => fetchDmCounterparts(conversations, user?.id),
    enabled: !!user && conversations.length > 0,
    staleTime: 1000 * 60,
  });

  // -------------------------------------------------------------------
  // Unread counts
  // -------------------------------------------------------------------
  const { data: unreadCounts = {} } = useQuery({
    queryKey: ['conversations-unread'],
    queryFn: async () => {
      const { data } = await getUnreadCounts();
      return data || {};
    },
    refetchInterval: 1000 * 30,
    staleTime: 1000 * 15,
  });

  // -------------------------------------------------------------------
  // Active conversation detail
  // -------------------------------------------------------------------
  const {
    data: activeConversation,
    isLoading: activeLoading,
    error: activeError,
    refetch: refetchActiveConversation,
  } = useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: async () => {
      const { data, error } = await getConversation(conversationId);
      if (error) throw error;
      return data;
    },
    enabled: !!conversationId,
  });

  // El detalle técnico (PostgREST/Supabase) es para el desarrollador, no para
  // el técnico en obra: se registra acá y la UI muestra un mensaje fijo.
  useEffect(() => {
    if (activeError) {
      // eslint-disable-next-line no-console
      console.error('[MessagesPage] fallo al cargar la conversación', activeError);
    }
  }, [activeError]);

  // Participants of the active conversation (includes last_read_at) — used
  // to derive the "read" (double check) indicator per own message. #446
  const { data: activeParticipants = [] } = useQuery({
    queryKey: ['conversation-participants', conversationId],
    queryFn: async () => {
      const { data, error } = await getParticipants(conversationId);
      if (error) throw error;
      return data || [];
    },
    enabled: !!conversationId,
    staleTime: 1000 * 10,
    refetchInterval: 1000 * 20,
  });

  const filteredConversations = useMemo(() => {
    const filter = TAB_FILTERS.find((t) => t.key === activeTab);
    if (!filter || !filter.type) return conversations;
    return conversations.filter((c) => c.type === filter.type);
  }, [conversations, activeTab]);

  // -------------------------------------------------------------------
  // Load messages for active conversation
  // -------------------------------------------------------------------
  const loadMessages = useCallback(
    async (before = null) => {
      if (!conversationId) return;
      const options = before ? { before } : {};
      const { data, hasMore: more, error } = await getMessages(
        conversationId,
        50,
        options
      );
      if (error) {
        console.error('[MESSAGES] loadMessages error:', error);
        return;
      }
      if (before) {
        setMessages((prev) => [...(data || []), ...prev]);
      } else {
        setMessages(data || []);
      }
      setHasMore(!!more);
    },
    [conversationId]
  );

  useEffect(() => {
    let cancelled = false;
    if (!conversationId) {
      setMessages([]);
      setHasMore(false);
      return undefined;
    }

    const init = async () => {
      setMessagesLoading(true);
      await loadMessages();
      if (!cancelled) {
        setMessagesLoading(false);
        // Mark as read once loaded
        const { error } = await markConversationRead(conversationId);
        if (!error) {
          queryClient.invalidateQueries({ queryKey: ['conversations-unread'] });
        }
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, [conversationId, loadMessages, queryClient]);

  // -------------------------------------------------------------------
  // Realtime subscription — active conversation's messages.
  //
  // #447: now also handles UPDATE (message edited, or soft-deleted via
  // `is_deleted = true`) instead of only INSERT — previously another
  // participant editing or deleting a message only showed up after a
  // reload. On UPDATE we replace the existing message in place instead of
  // appending (and skip mark-as-read / conversations-list invalidation,
  // since an edit doesn't change what's unread or the conversation order).
  // -------------------------------------------------------------------
  useEffect(() => {
    if (!conversationId) return undefined;

    const sub = subscribeToConversation(conversationId, (message, eventType) => {
      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.id === message.id);
        if (idx === -1) {
          // An UPDATE for a message outside the currently-loaded page
          // (e.g. an older message got edited) — nothing to replace.
          if (eventType === 'UPDATE') return prev;
          return [...prev, message];
        }
        const next = [...prev];
        next[idx] = message;
        return next;
      });

      if (eventType === 'UPDATE') return;

      if (message.sender_id !== user?.id) {
        markConversationRead(conversationId)
          .then(() =>
            queryClient.invalidateQueries({
              queryKey: ['conversations-unread'],
            })
          )
          .catch(() => {});
      }
      // Bump conversations list ordering
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    });
    subscriptionRef.current = sub;

    return () => {
      if (subscriptionRef.current?.unsubscribe) {
        subscriptionRef.current.unsubscribe();
      }
    };
  }, [conversationId, user, queryClient]);

  // -------------------------------------------------------------------
  // Realtime subscription — conversation LIST (sidebar).
  //
  // #447: MessagesPage previously never called subscribeToConversations,
  // so the sidebar (ordering by last_message_at, unread badges for
  // conversations that are NOT the one currently open) only refreshed via
  // the 30s refetchInterval below on the unread-counts query — a new
  // message in another conversation could take up to 30s to show up here.
  // This subscribes once per session (not per open conversation) and
  // invalidates only the query keys each event can actually affect — see
  // subscribeToConversations' doc comment in chatService.js for why each
  // event maps to what it invalidates.
  // -------------------------------------------------------------------
  useEffect(() => {
    if (!user?.id) return undefined;

    const sub = subscribeToConversations(user.id, ({ table, eventType }) => {
      if (table === 'chat_messages' && eventType === 'INSERT') {
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
        queryClient.invalidateQueries({ queryKey: ['conversations-unread'] });
      } else if (table === 'chat_messages' && eventType === 'UPDATE') {
        // Edit/soft-delete elsewhere doesn't change ordering, but a
        // soft-delete can reduce an unread count.
        queryClient.invalidateQueries({ queryKey: ['conversations-unread'] });
      } else if (table === 'chat_participants' && eventType === 'INSERT') {
        // Added to a new conversation — the sidebar needs to list it.
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
      }
    });

    return () => {
      if (sub?.unsubscribe) sub.unsubscribe();
    };
  }, [user?.id, queryClient]);

  // NOTE (#509): there used to be a `repliedToMap` effect here that
  // re-queried chat_messages for every `reply_to` id and passed the result
  // down as `repliedToMap`. MessageList never declared or read that prop, and
  // `getMessages` already returns the replied-to row joined as
  // `message.reply_to` — which is what MessageBubble actually renders. The
  // whole thing was a dead extra round-trip on every message change; removed.

  // -------------------------------------------------------------------
  // Send message. ChatInput (src/components/chat/ChatInput.js) invokes
  // onSend(text, attachedFile) with two positional args — mirror ChatView's
  // handleSend so an attached file is actually uploaded. Previously this
  // only read a single `payload` arg and looked for an `attachment_urls`
  // shape that never arrived, so the image file was silently dropped before
  // it was ever uploaded, and an image-only message was never sent at all
  // (see issue #267).
  //
  // #451: the attachment is no longer image-only — documents get
  // `message_type: 'file'` instead of `'image'`.
  // -------------------------------------------------------------------
  const handleSend = async (content, attachedFile) => {
    if (!conversationId) return;

    const trimmed = (content || '').trim();
    if (!trimmed && !attachedFile) return;

    setSending(true);
    try {
      let attachments = [];
      let messageType = 'text';

      if (attachedFile) {
        const isImage = attachedFile.type?.startsWith('image/');
        const uploadResult = await uploadChatAttachment(attachedFile, conversationId);
        if (uploadResult.error) {
          throw new Error(isImage ? 'Error al subir la imagen' : 'Error al subir el archivo');
        }
        attachments = [{
          url: uploadResult.url,
          type: attachedFile.type,
          name: attachedFile.name,
          size: attachedFile.size,
        }];
        messageType = isImage ? 'image' : 'file';
      }

      const { data, error } = await sendMessage({
        conversation_id: conversationId,
        content: trimmed || null,
        message_type: messageType,
        attachments,
        reply_to_id: replyingTo?.id || null,
      });
      if (error) throw error;
      if (data) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === data.id)) return prev;
          return [...prev, data];
        });
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
      }
      // Clear reply mode after successful send
      setReplyingTo(null);
    } catch (err) {
      console.error('[MESSAGES] handleSend error:', err);
      toast.error(err?.message || 'No se pudo enviar el mensaje');
    } finally {
      setSending(false);
    }
  };

  const handleLoadMore = async () => {
    if (!hasMore || messagesLoading || messages.length === 0) return;
    setMessagesLoading(true);
    await loadMessages(messages[0].created_at);
    setMessagesLoading(false);
  };

  // -------------------------------------------------------------------
  // Conversation actions
  // -------------------------------------------------------------------
  const leaveMutation = useMutation({
    mutationFn: async () => {
      const { error } = await leaveConversation(conversationId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Saliste de la conversacion');
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      navigate('/mensajes');
    },
    onError: (err) => toast.error(err?.message || 'No se pudo salir'),
  });

  // Mute/unmute (#448 — ported from the retired /admin/mensajes surface).
  // `is_muted` lives on the caller's chat_participants row, so the state
  // comes from the conversations list (getConversations exposes `isMuted`),
  // not from getConversation (which reads chat_conversations).
  const muteMutation = useMutation({
    mutationFn: async ({ id, mute }) => {
      const { success, error } = await muteConversation(id, mute);
      if (!success) throw error || new Error('No se pudo actualizar');
      return mute;
    },
    onSuccess: (mute) => {
      toast.success(mute ? 'Conversacion silenciada' : 'Notificaciones activadas');
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: (err) =>
      toast.error(err?.message || 'No se pudo actualizar las notificaciones'),
  });

  const handleToggleMute = useCallback(
    (conversation, mute) => {
      muteMutation.mutate({ id: conversation.id, mute });
    },
    [muteMutation]
  );

  // Delete a conversation from the sidebar kebab (#448). Only the creator or
  // a conversation admin can — chatService.deleteConversation enforces it and
  // returns an error otherwise.
  const deleteConversationMutation = useMutation({
    mutationFn: async (conversation) => {
      const { success, error } = await deleteConversation(conversation.id);
      if (!success) throw error || new Error('No se pudo eliminar');
      return conversation.id;
    },
    onSuccess: (deletedId) => {
      toast.success('Conversacion eliminada');
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({ queryKey: ['conversations-unread'] });
      if (deletedId === conversationId) {
        navigate('/mensajes');
      }
    },
    onError: (err) => toast.error(err?.message || 'No se pudo eliminar'),
  });

  const addParticipantMutation = useMutation({
    mutationFn: async (userId) => {
      const { error } = await addParticipant(conversationId, userId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Participante anadido');
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      // La lista de participantes es su propia query: sin esto el recién
      // añadido seguía apareciendo como "disponible" para añadir.
      queryClient.invalidateQueries({
        queryKey: ['conversation-participants', conversationId],
      });
      setShowAddParticipant(false);
      setAddParticipantId('');
    },
    onError: (err) => toast.error(err?.message || 'No se pudo anadir'),
  });

  // -------------------------------------------------------------------
  // Reply / Share-to-bitacora / Delete handlers (per-message kebab actions)
  // -------------------------------------------------------------------
  const handleReply = useCallback((message) => {
    setReplyingTo(message);
  }, []);

  const handleCancelReply = useCallback(() => {
    setReplyingTo(null);
  }, []);

  const shareToBitacoraMutation = useMutation({
    mutationFn: async (message) => {
      const projectId = activeConversation?.project_id;
      if (!projectId) throw new Error('Esta conversacion no es de un proyecto');
      const { error } = await shareMessageToBitacora(message, { projectId });
      if (error) throw error;
      return { messageId: message.id };
    },
    onSuccess: ({ messageId }) => {
      toast.success('Compartido en bitacora');
      // Optimistic local marker so the badge appears immediately.
      const sharedAt = new Date().toISOString();
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId
            ? {
                ...m,
                metadata: {
                  ...(m.metadata || {}),
                  shared_to_bitacora_at: sharedAt,
                },
              }
            : m
        )
      );
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: (err) =>
      toast.error(err?.message || 'No se pudo compartir en bitacora'),
  });

  const handleShareToBitacora = useCallback(
    (message) => {
      if (!activeConversation?.project_id) {
        toast.error('Esta conversacion no es de un proyecto');
        return;
      }
      const ok = window.confirm(
        '¿Compartir este mensaje en la bitacora del proyecto?'
      );
      if (!ok) return;
      shareToBitacoraMutation.mutate(message);
    },
    [activeConversation, shareToBitacoraMutation]
  );

  const deleteMessageMutation = useMutation({
    mutationFn: async (message) => {
      const { error } = await deleteMessage(message.id);
      if (error) throw error;
      return { messageId: message.id };
    },
    onSuccess: ({ messageId }) => {
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
      toast.success('Mensaje eliminado');
    },
    onError: (err) => toast.error(err?.message || 'No se pudo eliminar'),
  });

  const handleDeleteMessage = useCallback(
    (message) => {
      const ok = window.confirm('¿Eliminar este mensaje?');
      if (!ok) return;
      deleteMessageMutation.mutate(message);
    },
    [deleteMessageMutation]
  );

  // Edit a message (own messages, 15-min window — #449). Mirrors ChatView's
  // handleEditMessage: applies the server-confirmed row locally and rethrows
  // on failure so MessageBubble's inline editor knows to stay open.
  const editMessageMutation = useMutation({
    mutationFn: async ({ message, content }) => {
      const { data, error } = await editMessage(message.id, content);
      if (error) throw error;
      return data;
    },
    onError: (err) => toast.error(err?.message || 'No se pudo editar el mensaje'),
  });

  const handleEditMessage = useCallback(
    async (message, newContent) => {
      const data = await editMessageMutation.mutateAsync({ message, content: newContent });
      setMessages((prev) => prev.map((m) => (m.id === message.id ? { ...m, ...data } : m)));
    },
    [editMessageMutation]
  );

  const canShareToBitacora = useMemo(
    () =>
      !!activeConversation &&
      activeConversation.type === CONVERSATION_TYPE.PROJECT &&
      !!activeConversation.project_id,
    [activeConversation]
  );

  const handleMarkRead = async () => {
    if (!conversationId) return;
    const { error } = await markConversationRead(conversationId);
    if (!error) {
      queryClient.invalidateQueries({ queryKey: ['conversations-unread'] });
      toast.success('Marcada como leida');
    }
  };

  // -------------------------------------------------------------------
  // Available users to add to a group
  // -------------------------------------------------------------------
  const { data: addableUsers = [] } = useQuery({
    queryKey: ['chat-users'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, avatar_url, role')
        .order('full_name', { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled: showAddParticipant,
    staleTime: 1000 * 60,
  });

  // `getConversation` lee `chat_conversations`: NUNCA trajo `participants`.
  // Los participantes vienen de su propia query (`getParticipants`, #446), que
  // ya estaba montada acá y no se usaba para esto. Con el Set vacío, "Añadir
  // personas" ofrecía gente que ya estaba adentro y el INSERT chocaba contra
  // la unique `chat_participants_unique (conversation_id, user_id)`.
  const existingParticipantIds = useMemo(() => {
    return new Set(activeParticipants.map((p) => p.user_id));
  }, [activeParticipants]);

  const addableFiltered = useMemo(
    () => addableUsers.filter((u) => !existingParticipantIds.has(u.id)),
    [addableUsers, existingParticipantIds]
  );

  // -------------------------------------------------------------------
  // Active conversation derived display
  // -------------------------------------------------------------------
  const activeOtherUser = useMemo(() => {
    if (!activeConversation || activeConversation.type !== CONVERSATION_TYPE.DM) {
      return null;
    }
    const other = activeParticipants.find((p) => p.user_id !== user?.id);
    return other?.user || null;
  }, [activeConversation, activeParticipants, user]);

  const activeTitle = useMemo(() => {
    if (!activeConversation) return '';
    if (activeConversation.type === CONVERSATION_TYPE.DM) {
      return activeOtherUser?.full_name || 'Mensaje directo';
    }
    return (
      activeConversation.name ||
      activeConversation.project?.title ||
      (activeConversation.type === CONVERSATION_TYPE.GROUP ? 'Grupo' : 'Proyecto')
    );
  }, [activeConversation, activeOtherUser]);

  const activeSubtitle = useMemo(() => {
    if (!activeConversation) return '';
    const count = activeParticipants.length;
    if (activeConversation.type === CONVERSATION_TYPE.DM) {
      return activeOtherUser?.role ? formatUserRole(activeOtherUser.role) : '';
    }
    return `${count} participante${count === 1 ? '' : 's'}`;
  }, [activeConversation, activeParticipants, activeOtherUser]);

  const isGroup = activeConversation?.type === CONVERSATION_TYPE.GROUP;

  // Mute flag for the open conversation. It lives on the participant row, so
  // it only comes back from the conversations LIST query — getConversation
  // (chat_conversations) has no is_muted column.
  const activeIsMuted = useMemo(
    () => !!conversations.find((c) => c.id === conversationId)?.isMuted,
    [conversations, conversationId]
  );

  // -------------------------------------------------------------------
  // Search bar (#427) — select a conversation or jump to a specific message
  // -------------------------------------------------------------------
  const handleSelectConversation = (id) => {
    navigate(`/mensajes/${id}`);
  };

  const handleSelectSearchMessage = useCallback(
    (message) => {
      setHighlightMessageId(message.id);
      navigate(`/mensajes/${message.conversation_id}`);
    },
    [navigate]
  );

  // Scroll to + briefly flash the target message once it's rendered in the
  // (currently loaded) message list. Best-effort: if the message is older
  // than the last 50 loaded, it simply won't scroll — opening the right
  // conversation is still the acceptance-critical behavior.
  useEffect(() => {
    if (!highlightMessageId || messagesLoading) return undefined;
    const el = document.getElementById(`chat-message-${highlightMessageId}`);
    if (!el) return undefined;

    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('message-highlighted');
    setHighlightMessageId(null);

    const timer = setTimeout(() => el.classList.remove('message-highlighted'), 1800);
    return () => clearTimeout(timer);
  }, [highlightMessageId, messages, messagesLoading]);

  const handleBackToList = () => {
    navigate('/mensajes');
  };

  return (
    <DashboardLayout>
    <div
      className={`messages-shell ${conversationId ? 'has-active-chat' : ''}`}
    >
      {/* Sidebar */}
      <aside className="messages-sidebar">
        <header className="messages-sidebar-header">
          <h2 className="messages-sidebar-title">
            <span>Mensajes</span>
            <span className="messages-sidebar-actions">
              <button
                type="button"
                className="messages-icon-btn"
                title="Nuevo mensaje directo"
                aria-label="Nuevo mensaje directo"
                onClick={() => setShowNewDm(true)}
              >
                <i className="bi bi-plus-circle"></i>
              </button>
              <button
                type="button"
                className="messages-icon-btn"
                title="Nuevo canal de proyecto"
                aria-label="Nuevo canal de proyecto"
                onClick={() => setShowNewChannel(true)}
              >
                <i className="bi bi-hash"></i>
              </button>
              <button
                type="button"
                className="messages-icon-btn"
                title="Nuevo grupo"
                aria-label="Nuevo grupo"
                onClick={() => setShowNewGroup(true)}
              >
                <i className="bi bi-people"></i>
              </button>
            </span>
          </h2>
        </header>

        <MessagesSearchBar
          conversations={conversations}
          onSelectConversation={handleSelectConversation}
          onSelectMessage={handleSelectSearchMessage}
          onActiveChange={setSearchActive}
        />

        {!searchActive && (
          <>
            <div className="messages-tabs">
              {TAB_FILTERS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={`messages-tab ${activeTab === tab.key ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="messages-list">
              {convLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : convError ? (
                <Alert variant="danger" className="m-3">
                  No se pudieron cargar las conversaciones.
                </Alert>
              ) : filteredConversations.length === 0 ? (
                <div className="messages-list-empty">
                  <i className="bi bi-chat-dots"></i>
                  <div className="messages-list-empty-title">
                    Aun no tienes conversaciones
                  </div>
                  <p className="messages-list-empty-text">
                    Inicia una con &quot;Nuevo DM&quot; o &quot;Nuevo grupo&quot;.
                  </p>
                </div>
              ) : (
                filteredConversations.map((c) => (
                  <ConversationRow
                    key={c.id}
                    conversation={c}
                    isActive={c.id === conversationId}
                    unread={unreadCounts[c.id] || 0}
                    otherUser={dmCounterparts[c.id]}
                    onClick={() => handleSelectConversation(c.id)}
                    onToggleMute={handleToggleMute}
                    onDelete={setDeleteTarget}
                  />
                ))
              )}
            </div>
          </>
        )}
      </aside>

      {/* Chat area */}
      <section className="messages-chat">
        {!conversationId ? (
          <div className="messages-chat-empty">
            <i className="bi bi-chat-square-text"></i>
            <h3>Selecciona una conversacion</h3>
            <p>
              Elige una conversacion del panel izquierdo, o inicia una nueva
              con los botones de la parte superior.
            </p>
          </div>
        ) : activeLoading ? (
          <div className="messages-chat-empty">
            <Spinner animation="border" />
          </div>
        ) : activeError ? (
          /* `activeError.message` era texto de PostgREST/Supabase puesto tal
             cual delante del usuario. Se reemplaza por un mensaje fijo y una
             salida real; el detalle técnico va al console.error de abajo. */
          <div className="messages-chat-empty" data-testid="messages-chat-error">
            <i className="bi bi-exclamation-triangle"></i>
            <h3>No se pudo cargar la conversación</h3>
            <p>
              Hubo un problema de conexión con el servidor. Volvé a intentarlo; si el
              problema sigue, avisá al administrador.
            </p>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => refetchActiveConversation()}
              data-testid="messages-chat-error-retry"
            >
              <i className="bi bi-arrow-clockwise me-1" aria-hidden="true"></i>
              Reintentar
            </Button>
          </div>
        ) : (
          <>
            <div className="messages-chat-header">
              <button
                type="button"
                className="messages-chat-header-back d-md-none"
                onClick={handleBackToList}
                aria-label="Volver"
              >
                <i className="bi bi-arrow-left"></i>
              </button>
              <div className="messages-chat-header-info">
                <h2 className="messages-chat-header-title">{activeTitle}</h2>
                <p className="messages-chat-header-subtitle">{activeSubtitle}</p>
              </div>

              <div className="messages-chat-header-actions">
                {activeParticipants.length > 0 && (
                  <span className="hex-avatar-stack">
                    {activeParticipants.slice(0, 3).map((p) => (
                      <TaskAssigneeAvatar
                        key={p.id || p.user_id}
                        assignee={p.user}
                        size="sm"
                      />
                    ))}
                  </span>
                )}

                <Dropdown align="end">
                  <Dropdown.Toggle
                    as="button"
                    className="messages-chat-header-icon-btn"
                    aria-label={`Opciones de la conversación ${activeTitle}`}
                  >
                    <i className="bi bi-three-dots-vertical"></i>
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => setShowInfoModal(true)}>
                      <i className="bi bi-info-circle me-2"></i>
                      Ver participantes
                    </Dropdown.Item>
                    {isGroup && (
                      <Dropdown.Item
                        onClick={() => setShowAddParticipant(true)}
                      >
                        <i className="bi bi-person-plus me-2"></i>
                        Anadir personas
                      </Dropdown.Item>
                    )}
                    <Dropdown.Item onClick={handleMarkRead}>
                      <i className="bi bi-check2-all me-2"></i>
                      Marcar como leido
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() =>
                        handleToggleMute(
                          { id: conversationId },
                          !activeIsMuted
                        )
                      }
                    >
                      <i
                        className={`bi ${
                          activeIsMuted ? 'bi-bell' : 'bi-bell-slash'
                        } me-2`}
                      ></i>
                      {activeIsMuted ? 'Activar notificaciones' : 'Silenciar'}
                    </Dropdown.Item>
                    {canShareToBitacora && (
                      <Dropdown.Item onClick={() => setShowBitacoraModal(true)}>
                        <i className="bi bi-journal-bookmark me-2"></i>
                        Vincular a bitacora
                      </Dropdown.Item>
                    )}
                    <Dropdown.Divider />
                    <Dropdown.Item
                      onClick={() => leaveMutation.mutate()}
                      className="text-danger"
                    >
                      <i className="bi bi-box-arrow-right me-2"></i>
                      Salir de la conversacion
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>

            <MessageList
              messages={messages}
              currentUserId={user?.id}
              participants={activeParticipants}
              loading={messagesLoading}
              hasMore={hasMore}
              onLoadMore={handleLoadMore}
              onReply={handleReply}
              onShareToBitacora={handleShareToBitacora}
              onDelete={handleDeleteMessage}
              onEdit={handleEditMessage}
              canShareToBitacora={canShareToBitacora}
            />

            <ChatInput
              onSend={handleSend}
              sending={sending}
              disabled={!activeConversation}
              placeholder="Escribe un mensaje..."
              conversationId={conversationId}
              replyTo={replyingTo}
              onCancelReply={handleCancelReply}
            />
          </>
        )}
      </section>

      {/* New DM modal */}
      <NewDmModal
        show={showNewDm}
        onHide={() => setShowNewDm(false)}
        onConversationOpen={(id) => navigate(`/mensajes/${id}`)}
      />

      {/* New group modal */}
      <NewGroupModal
        show={showNewGroup}
        onHide={() => setShowNewGroup(false)}
        onConversationOpen={(id) => navigate(`/mensajes/${id}`)}
      />

      {/* New project channel modal (#444) */}
      <NewChannelModal
        show={showNewChannel}
        onHide={() => setShowNewChannel(false)}
        onConversationOpen={(id) => navigate(`/mensajes/${id}`)}
      />

      {/* Link the whole conversation to the project bitacora (#448) */}
      {activeConversation && (
        <BitacoraLinkModal
          show={showBitacoraModal}
          onHide={() => setShowBitacoraModal(false)}
          conversation={activeConversation}
        />
      )}

      {/* Delete conversation confirmation (#448) */}
      <Modal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title as="h6">Eliminar conversacion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            {deleteTarget?.type === CONVERSATION_TYPE.DM
              ? 'Se eliminara este chat. Los mensajes se conservaran.'
              : `Se eliminara ${
                  deleteTarget?.type === CONVERSATION_TYPE.PROJECT
                    ? 'este canal'
                    : 'este grupo'
                } para todos los participantes.`}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            disabled={deleteConversationMutation.isLoading}
            onClick={() => {
              deleteConversationMutation.mutate(deleteTarget);
              setDeleteTarget(null);
            }}
          >
            {deleteConversationMutation.isLoading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <>
                <i className="bi bi-trash me-2"></i>
                Eliminar
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Participants info modal */}
      <Modal
        show={showInfoModal}
        onHide={() => setShowInfoModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Participantes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {activeParticipants.length === 0 ? (
            <p className="text-muted text-center mb-0">Sin participantes.</p>
          ) : (
            <div className="messages-user-list">
              {activeParticipants.map((p) => (
                <div key={p.id || p.user_id} className="messages-user-row">
                  <TaskAssigneeAvatar assignee={p.user} size="md" />
                  <div className="messages-user-info">
                    <div className="messages-user-name">
                      {p.user?.full_name || 'Usuario'}
                    </div>
                    <div className="messages-user-role">
                      {formatUserRole(p.user?.role)}
                      {p.role === 'admin' && (
                        <span className="ms-2 badge bg-primary">Admin</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowInfoModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add participant modal (groups only) */}
      <Modal
        show={showAddParticipant}
        onHide={() => setShowAddParticipant(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Anadir participante</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {addableFiltered.length === 0 ? (
            <p className="text-muted text-center mb-0">
              No hay mas usuarios disponibles.
            </p>
          ) : (
            <Form>
              <Form.Group>
                <Form.Label>Selecciona un usuario</Form.Label>
                <Form.Select
                  value={addParticipantId}
                  onChange={(e) => setAddParticipantId(e.target.value)}
                >
                  <option value="">-- Selecciona --</option>
                  {addableFiltered.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.full_name} ({formatUserRole(u.role)})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowAddParticipant(false)}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={!addParticipantId || addParticipantMutation.isLoading}
            onClick={() => addParticipantMutation.mutate(addParticipantId)}
          >
            {addParticipantMutation.isLoading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <>
                <i className="bi bi-person-plus me-2"></i>
                Anadir
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
    </DashboardLayout>
  );
};

export default MessagesPage;
